import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  DateSelectArg,
  DateSpanApi,
  DatesSetArg,
  DayHeaderContentArg,
  EventApi,
  EventChangeArg,
  EventContentArg,
  EventInput,
  EventRemoveArg,
  EventSourceInput,
  SlotLabelContentArg,
} from '@fullcalendar/core'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridWeek from '@fullcalendar/timegrid'
import { addMinutes, differenceInMinutes, parseISO, subMinutes } from 'date-fns'
import { format, formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { isEmpty, isNil, partition, sortBy } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'
import { v4 as uuidv4 } from 'uuid'

import {
  buildDefaultEventTitle,
  eventsOverlap,
  getAdjustedEvent,
  getProportionalContentHeight,
  isBookableEvent,
  isGenericEvent,
  isOOOEvent,
  isSessionOrSlot,
  isWithinWorkingHours,
  projectWorkingHoursToDisplayTimeZone,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import {
  COMMON_MIXPANEL_EVENTS,
  COMMON_MIXPANEL_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  getTimeZoneName,
  toLocalISOString,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  AppointmentBookedDetails,
  BookableEventExtendedProps,
  BuiltEvent,
  CalendarAlert,
  CalendarAlertArgs,
  CalendarProvider,
  ConflictStrategy,
  EventExtendedProps,
  EventModality,
  isBendEvent,
  isSameBookingType,
  isSameModality,
  LyraEventExtendedPropsNoTZ,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Note } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { Title } from '../../atoms/title/Title'
import { AppContext } from '../../context'
import { ProviderCalendarAlerts } from '../../molecules/providerCalendarAlerts/ProviderCalendarAlerts'
import { ProviderCalendarEvent } from '../../molecules/providerCalendarEvent/ProviderCalendarEvent'
import { ProviderCalendarHeader } from '../../molecules/providerCalendarHeader/ProviderCalendarHeader'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { CalendarClient, GetBookingDetailsForClient } from '../../ui-models/clients/CalendarClient'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { CalendarTimeZoneSelector } from '../calendarTimeZoneSelector/CalendarTimeZoneSelector'
import { ConfirmationModal } from '../confirmationModal/ConfirmationModal'
import { ProviderCalendarEditSessionModality } from '../providerCalendarEditSessionModality/ProviderCalendarEditSessionModality'
import { ProviderCalendarOpenSlotModal } from '../providerCalendarOpenSlotModal/ProviderCalendarOpenSlotModal'
import { ProviderCalendarScheduleSessionModal } from '../providerCalendarScheduleSessionModal/ProviderCalendarScheduleSessionModal'
import { ProviderCalendarDateSlotSelectionModal } from '../providerCalendarSlotSelectionModal/ProviderCalendarDateSlotSelectionModal'

const SLOT_WIDTH_WITH_TIMEZONE_SELECTOR = 78

export type ProviderCalendarProps = {
  timeZone?: string
  onTimeZoneChange?: (timeZone: string) => void
  onSettingsPressed?: () => void
  loading?: boolean
  getEvents?: (params: {
    startDate: string
    endDate: string
    isLoadingPrimaryCalendar?: boolean
  }) => Promise<EventInput[]>
  onClientProfilePressed?: (clientId: string) => void
  onEventMoved?: (
    event: {
      externalId: string
      startTime: string
      endTime: string
      modality?: EventModality | null
      oldModality?: EventModality | null
      title?: string
      booking_type?: string[] | null
    },
    revert: () => void,
  ) => void
  onEventsAdded?: (
    events: BuiltEvent[],
    revert: () => void,
    attachEventID: (events: { id: string; start: { dateTime: string; timeZone: string } }[]) => void,
    entryPoint: 'calendar' | 'add button' | null,
  ) => void
  onEventDeleted?: (event: { externalId: string }, revert: () => void) => void
  getAppointmentBookedDetails?: (appointmentId: number) => Promise<AppointmentBookedDetails>
  onOpenInGoogleCalendar?: (externalId: string, calendarConfigurationId: string) => void
  onOpenVideoConferencing?: (externalId: string, calendarConfigurationId: string) => void
  onOpenZendeskPressed?: () => void
  onCancelPressed?: (appointmentId: number) => void
  onCancelMosAppointmentPressed?: (appointmentId: number) => void
  onOpenPopover?: ({ appointmentId, clientId }: { appointmentId: number; clientId: string }) => void
  onReschedule?: (appointmentId: number) => void
  onCloseSession?: (appointmentId: number, clientId: string | null) => void
  onCompleteNote?: (appointmentId: number, clientId: string) => void
  onViewCompletedNote?: (appointmentId: number) => void
  getAlerts?: (args: CalendarAlertArgs) => CalendarAlert[]
  hidePerformanceMetrics?: boolean
  hybridBookingMode?: boolean
  hideCalendarEventDetails?: boolean
  displayAppointmentBookedDetails?: boolean
  showRiskAlerts?: boolean
  calendarProvider?: CalendarProvider
  showModalitySelection?: boolean
  inPersonPreference?: boolean
  providerAddresses?: Address[] | null
  videoPreference?: boolean
  // When true, booked-event video modality is labeled "Virtual" instead of "Video".
  renderVideoAsVirtual?: boolean
  engageDAProvider?: boolean
  supportsOnOffPlatformVideo?: boolean
  providerNotes?: Note[]
  notesFetched?: boolean
  showSessionSubmission?: boolean
  getSessionsAwaitingSubmissionCount?: (pastSessionApptIds: number[]) => number | null
  clients?: CalendarClient[]
  suggestedClients?: CalendarClient[]
  getBookingDetailsForClient?: GetBookingDetailsForClient
  onScheduleSession?: (event: {
    startTime: string
    endTime: string
    slotEndTime: string
    client: string
    localStartTime: string
    localEndTime: string
    localSlotEndTime: string
    duration: number
    meetingFormat: string
    episode?: Episode
    recurring: boolean
    frequency: number
    repeatFor: number
    address?: string
    videoPlatform?: VIDEO_PLATFORM
    sessionType?: string
  }) => Promise<void>
  trackScheduleNewSessionEntryPoint?: (entryPoint: string) => void
  rightHeaderSection?: React.ReactNode
  enableBackdatedAppointments?: boolean
  isSelfPayEnabled?: boolean
  showLateCancellations?: boolean
  clientVoicemailConsent?: boolean
  guardianVoicemailConsent?: boolean
  guardianPhone?: string
  clientPronouns?: string[]
  isClientDataLoading?: boolean
  isNPSession?: boolean
  flexibleAvailabilityWindows?: boolean
  /** Days to hide from calendar (0=Sunday, 6=Saturday) */
  hiddenDays?: number[]
  /**
   * Minimum time slot to display in the calendar view.
   * Only set for BEND providers with explicit role config dailyStart.
   * Regular providers should leave this undefined to show full 24-hour view.
   * Format: 'HH:mm:ss' (e.g., '07:00:00' for 7am)
   */
  slotMinTime?: string
  /**
   * Maximum time slot to display in the calendar view.
   * Only set for BEND providers with explicit role config dailyEnd.
   * Regular providers should leave this undefined to show full 24-hour view.
   * Format: 'HH:mm:ss' (e.g., '19:00:00' for 7pm, '00:00:00' for midnight)
   */
  slotMaxTime?: string
  isLegacyProvider?: boolean
  isLegacyClientListLoading?: boolean
  onUpdateModality?: (
    appointmentIdsToUpdate: number[],
    meetingFormat: MEETING_FORMATS,
    address?: string,
    videoPlatform?: VIDEO_PLATFORM,
  ) => Promise<void>
  getUpcomingAppointmentIds?: (clientId: string) => Promise<number[]>
  enableSessionModalityEditability?: boolean
  conflictStrategy?: ConflictStrategy
  conflictTooltipText?: string
  backgroundAvailability?: boolean
  splitVideoOptions?: boolean
  providerVideoPlatform?: VIDEO_PLATFORM | null
}

const Container = styled.View<{ lowOpacity: boolean }>(({ lowOpacity }) => ({
  opacity: lowOpacity ? 0.4 : 1,
  overflow: 'scroll',
  height: '100%',
}))

const DayContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing['4px'],
  padding: theme.spacing['12px'],
  width: '100%',
}))

const TodayBorder = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 4,
  backgroundColor: theme.colors.borderFocus,
}))

const LoadingView = styled.View({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '100%',
  zIndex: 2,
  justifyContent: 'center',
})

export type ProviderCalendarRef = {
  updateAppointments: (appointments: { [key: number]: LyraEventExtendedPropsNoTZ }) => void
  refresh: () => void
}

const DEFAULT_HOURS_COUNT = 24
const DEFAULT_CONTENT_HEIGHT = 2000

export const ProviderCalendar = forwardRef<ProviderCalendarRef, ProviderCalendarProps>(
  (
    {
      onSettingsPressed,
      timeZone = 'America/Los_Angeles',
      onTimeZoneChange,
      loading = false,
      getEvents,
      getAppointmentBookedDetails,
      onClientProfilePressed,
      onEventMoved,
      onEventsAdded,
      onEventDeleted,
      onOpenInGoogleCalendar,
      onOpenVideoConferencing,
      onOpenZendeskPressed,
      onCancelPressed,
      onCancelMosAppointmentPressed,
      onOpenPopover,
      onReschedule,
      onCloseSession,
      onCompleteNote,
      onViewCompletedNote,
      getAlerts,
      hidePerformanceMetrics,
      hybridBookingMode,
      hideCalendarEventDetails,
      displayAppointmentBookedDetails,
      showRiskAlerts,
      calendarProvider,
      showModalitySelection,
      inPersonPreference,
      videoPreference,
      renderVideoAsVirtual,
      providerAddresses,
      engageDAProvider,
      supportsOnOffPlatformVideo,
      providerNotes,
      notesFetched,
      showSessionSubmission,
      getSessionsAwaitingSubmissionCount,
      clients,
      suggestedClients,
      getBookingDetailsForClient,
      onScheduleSession,
      trackScheduleNewSessionEntryPoint,
      rightHeaderSection,
      enableBackdatedAppointments,
      isSelfPayEnabled,
      showLateCancellations,
      clientVoicemailConsent,
      guardianVoicemailConsent,
      guardianPhone,
      clientPronouns,
      isClientDataLoading,
      isNPSession,
      flexibleAvailabilityWindows,
      hiddenDays,
      slotMinTime,
      slotMaxTime,
      isLegacyProvider,
      isLegacyClientListLoading,
      onUpdateModality,
      getUpcomingAppointmentIds,
      enableSessionModalityEditability,
      conflictStrategy = ConflictStrategy.DEFAULT,
      conflictTooltipText,
      backgroundAvailability = false,
      splitVideoOptions,
      providerVideoPlatform,
    },
    ref,
  ) => {
    const intl = useIntl()
    const theme = useTheme() as ThemeType
    const { showIPCSessionCreate, isTimezoneSelectorEnabled } = useFlags()
    const { showCalendarScheduleSession } = useContext(AppContext)

    const nowInTimeZone = useMemo(() => formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd'T'HH:mm:ss"), [timeZone])
    // For the current calendar view:
    // - BEND providers pass explicit slotMinTime/slotMaxTime props from their role config
    // - Regular providers leave these undefined to see full 24-hour calendar (external events can be anywhere)
    const workingHoursStart = calendarProvider?.daily_start ?? '07:00:00'
    const workingHoursEnd = calendarProvider?.daily_end ?? '22:00:00'
    const providerTimeZone = calendarProvider?.timezone ?? timeZone

    const resolvedMinTime = slotMinTime ?? '00:00:00'
    const resolvedMaxTime = slotMaxTime === '00:00:00' ? '24:00:00' : (slotMaxTime ?? '24:00:00')
    const contentHeight = getProportionalContentHeight(
      resolvedMinTime,
      resolvedMaxTime,
      DEFAULT_HOURS_COUNT,
      DEFAULT_CONTENT_HEIGHT,
    )

    const calendarRef = useRef<FullCalendar>(null)
    const startingRange = useRef<{ startStr: string; endStr: string } | null>(null)
    const [axisElement, setAxisElement] = useState<Element | null>(null)
    const [scheduledSessions, setScheduledSessions] = useState<number | null>(null)
    const [pastSessionApptIds, setPastSessionApptIds] = useState<number[]>([])
    const [clientHours, setClientHours] = useState<number | null>(null)
    const [rangeToAdd, setRangeToAdd] = useState<{
      start: string
      end: string
      startLocal: string
      endLocal: string
    } | null>(null)
    const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null)
    const [timeOffHours, setTimeOffHours] = useState<number | null>(null)
    const [alertsToShow, setAlertsToShow] = useState<CalendarAlert[]>([])
    const [startDate, setStartDate] = useState<string | null>(null)
    const [overlappingSlots, setOverlappingSlots] = useState<string[] | null>(null)
    const [showAddEventModal, setShowAddEventModal] = useState(false)
    const [showOpenSlotModal, setShowOpenSlotModal] = useState(false)
    const [showScheduleSessionModal, setShowScheduleSessionModal] = useState(false)
    const [slotVideoPlatform, setSlotVideoPlatform] = useState<VIDEO_PLATFORM | null>(null)
    const [showEditModalityModal, setShowEditModalityModal] = useState(false)
    const [upcomingAppointmentIds, setUpcomingAppointmentIds] = useState<number[] | undefined>(undefined)
    const [isLoadingUpcomingAppointmentIds, setIsLoadingUpcomingAppointmentIds] = useState(false)
    const [eventToDelete, setEventToDelete] = useState<EventContentArg | null>(null)
    const [eventToEdit, setEventToEdit] = useState<EventContentArg | null>(null)
    const [initialModalEvents, setInitialModalEvents] = useState<EventApi[] | null>(null)
    const isConfirmedEventRemovalRef = useRef(false)
    const addEventEntryPointRef = useRef<'calendar' | 'add button' | null>(null)
    const reopenEventPopoverRef = useRef<(() => void) | null>(null)
    const viewElRef = useRef<HTMLElement | null>(null)
    const { trackEvent } = useContext(AppContext)

    const sessionsAwaitingSubmission = useMemo(() => {
      if (!engageDAProvider || !getSessionsAwaitingSubmissionCount) {
        return
      }
      return getSessionsAwaitingSubmissionCount(pastSessionApptIds)
    }, [engageDAProvider, getSessionsAwaitingSubmissionCount, pastSessionApptIds])

    // Update axis element content when timezone changes (for non-selector mode)
    useEffect(() => {
      if (axisElement && !(isTimezoneSelectorEnabled && onTimeZoneChange)) {
        axisElement.innerHTML = getTimeZoneName({ timeZone, timeZoneName: 'short' }) ?? ''
      }
    }, [axisElement, isTimezoneSelectorEnabled, onTimeZoneChange, timeZone])

    const onDatesSet = useCallback((dates: DatesSetArg) => {
      setDateRange(dates)
    }, [])

    const dayContentRenderer = useCallback(
      ({ date, isToday }: DayHeaderContentArg) => {
        const newDate = addMinutes(date, date.getTimezoneOffset())
        return (
          <DayContentContainer>
            <Title
              size={TitleSize.XSMALL}
              text={format(newDate, 'eee d', { timeZone })}
              color={isToday ? theme.colors.textActive : undefined}
            />
            {isToday && <TodayBorder />}
          </DayContentContainer>
        )
      },
      [theme.colors.textActive, timeZone],
    )

    const slotLabelContent = useCallback(
      ({ text }: SlotLabelContentArg) => {
        // When timezone selector is enabled, use a wider container so FullCalendar
        // calculates a larger shrink width for the axis column
        const minWidth = isTimezoneSelectorEnabled && onTimeZoneChange ? SLOT_WIDTH_WITH_TIMEZONE_SELECTOR : undefined
        return (
          <div style={{ minWidth }}>
            <BodyText color={theme.colors.textSecondary} size={BodyTextSize.SMALL} text={text} />
          </div>
        )
      },
      [isTimezoneSelectorEnabled, onTimeZoneChange, theme.colors.textSecondary],
    )

    const pendingUpcomingApptClientIdRef = useRef<string | null>(null)

    const handleOpenEditModality = useCallback(
      async (arg: EventContentArg, reopenCallback?: () => void) => {
        reopenEventPopoverRef.current = reopenCallback ?? null
        setEventToEdit(arg)
        setUpcomingAppointmentIds(undefined)
        setShowEditModalityModal(true)
        const clientId = arg.event.extendedProps.clientId
        if (clientId && getUpcomingAppointmentIds) {
          pendingUpcomingApptClientIdRef.current = clientId
          setIsLoadingUpcomingAppointmentIds(true)
          const ids = await getUpcomingAppointmentIds(clientId)
          if (pendingUpcomingApptClientIdRef.current === clientId) {
            setUpcomingAppointmentIds(ids)
            setIsLoadingUpcomingAppointmentIds(false)
          }
        } else {
          pendingUpcomingApptClientIdRef.current = null
          setUpcomingAppointmentIds(undefined)
          setIsLoadingUpcomingAppointmentIds(false)
        }
      },
      [getUpcomingAppointmentIds],
    )

    const getEventClassNames = useCallback((args: EventContentArg) => {
      if (args.event.id === 'workingHours') {
        return ['lc-working-hours-bg']
      }
      const props = args.event.extendedProps as EventExtendedProps
      if (isGenericEvent(props)) {
        return ['lc-generic-event']
      }
      return []
    }, [])

    const eventContent = useCallback(
      (args: EventContentArg) => {
        if (!args.event.startStr || !args.event.endStr) {
          return null
        }

        const eventProperties = args.event.extendedProps as EventExtendedProps
        const appointmentId =
          eventProperties.lyraEventType === 'session' && !isBendEvent(eventProperties)
            ? eventProperties.lyraAppointmentId
            : null
        const noteNeeded =
          appointmentId && notesFetched
            ? !providerNotes?.find((note) => note.appointment_id === appointmentId && note.status === 'completed')
            : false

        return (
          <ProviderCalendarEvent
            args={args}
            startingRange={startingRange}
            timeZone={timeZone}
            getAppointmentBookedDetails={getAppointmentBookedDetails}
            onClientProfilePressed={onClientProfilePressed}
            onOpenInGoogleCalendar={onOpenInGoogleCalendar}
            onOpenVideoConferencing={onOpenVideoConferencing}
            showModalitySelection={showModalitySelection}
            onDelete={(arg: EventContentArg) => setEventToDelete(arg)}
            onEdit={(arg: EventContentArg) => {
              setShowAddEventModal(true)
              setEventToEdit(arg)
              setInitialModalEvents(calendarRef.current && calendarRef.current.getApi().getEvents())
            }}
            hideCalendarEventDetails={hideCalendarEventDetails}
            hybridBookingMode={hybridBookingMode}
            displayAppointmentBookedDetails={displayAppointmentBookedDetails}
            showRiskAlerts={showRiskAlerts}
            onCancelPressed={onCancelPressed}
            onCancelMosAppointmentPressed={onCancelMosAppointmentPressed}
            onOpenPopover={onOpenPopover}
            onReschedule={onReschedule}
            onCloseSession={onCloseSession}
            onCompleteNote={onCompleteNote}
            onViewCompletedNote={onViewCompletedNote}
            engageDAProvider={engageDAProvider}
            supportsOnOffPlatformVideo={supportsOnOffPlatformVideo}
            enableSessionModalityEditability={enableSessionModalityEditability}
            sessionNeedsNote={noteNeeded}
            showLateCancellations={showLateCancellations}
            onScheduleSession={() => {
              trackScheduleNewSessionEntryPoint?.('Bookable slot')
              // startStr is "fake UTC"; see toFakeUTCISOString docs
              setRangeToAdd({
                start: args.event.startStr,
                end: args.event.endStr,
                startLocal: args.event.startStr,
                endLocal: args.event.endStr,
              })
              const eventProps = args.event.extendedProps as EventExtendedProps
              setSlotVideoPlatform(eventProps.modality_availability?.video?.platform ?? null)
              setShowScheduleSessionModal(true)
            }}
            clientVoicemailConsent={clientVoicemailConsent}
            guardianVoicemailConsent={guardianVoicemailConsent}
            guardianPhone={guardianPhone}
            clientPronouns={clientPronouns}
            isClientDataLoading={isClientDataLoading}
            flexibleAvailabilityWindows={flexibleAvailabilityWindows}
            inPersonPreference={inPersonPreference}
            videoPreference={videoPreference}
            renderVideoAsVirtual={renderVideoAsVirtual}
            handleOpenEditModality={handleOpenEditModality}
            conflictTooltipText={conflictTooltipText}
          />
        )
      },
      [
        notesFetched,
        providerNotes,
        timeZone,
        getAppointmentBookedDetails,
        onClientProfilePressed,
        onOpenInGoogleCalendar,
        onOpenVideoConferencing,
        showModalitySelection,
        flexibleAvailabilityWindows,
        hideCalendarEventDetails,
        hybridBookingMode,
        displayAppointmentBookedDetails,
        showRiskAlerts,
        onCancelPressed,
        onCancelMosAppointmentPressed,
        onOpenPopover,
        onReschedule,
        onCloseSession,
        onCompleteNote,
        onViewCompletedNote,
        engageDAProvider,
        enableSessionModalityEditability,
        showLateCancellations,
        trackScheduleNewSessionEntryPoint,
        clientVoicemailConsent,
        guardianVoicemailConsent,
        guardianPhone,
        clientPronouns,
        isClientDataLoading,
        inPersonPreference,
        videoPreference,
        renderVideoAsVirtual,
        handleOpenEditModality,
        conflictTooltipText,
      ],
    )

    const getEventContent = useCallback(
      (args: EventContentArg) => (args.event.id === 'workingHours' ? { domNodes: [] } : eventContent(args)),
      [eventContent],
    )

    useEffect(() => {
      if (!calendarRef.current) {
        return
      }
      const calendar = calendarRef.current.getApi()
      setDateRange({ start: calendar.view.currentStart, end: calendar.view.currentEnd })
      calendar.on('datesSet', onDatesSet)

      return () => {
        calendar.off('datesSet', onDatesSet)
      }
    }, [onDatesSet])

    const rangeText = useMemo(() => {
      if (!dateRange) {
        return ''
      }

      const startDate = addMinutes(dateRange.start, dateRange.start.getTimezoneOffset())
      const endDate = subMinutes(addMinutes(dateRange.end, dateRange.end.getTimezoneOffset()), 1)
      const startMonth = intl.formatDate(startDate, { month: 'short', timeZone })
      const endMonth = intl.formatDate(endDate, { month: 'short', timeZone })
      const startYear = intl.formatDate(startDate, { year: 'numeric', timeZone })
      const endYear = intl.formatDate(endDate, { year: 'numeric', timeZone })
      if (startMonth === endMonth && startYear === endYear) {
        return intl.formatDate(endDate, { month: 'long', year: 'numeric', timeZone })
      }
      const startDateFormatted = intl.formatDate(startDate, {
        month: 'short',
        year: startYear === endYear ? undefined : 'numeric',
        timeZone,
      })

      const endDateFormatted = intl.formatDate(endDate, { month: 'short', year: 'numeric', timeZone })
      return `${startDateFormatted} - ${endDateFormatted}`
    }, [dateRange, intl, timeZone])

    const onSelectAllow = useCallback(
      (args: DateSpanApi) => {
        if (zonedTimeToUtc(parseISO(args.startStr), timeZone) < new Date()) {
          return false
        }
        if (
          !isWithinWorkingHours({
            event: { start: args.startStr, end: args.endStr },
            workingHours: { start: workingHoursStart, end: workingHoursEnd },
            timeZone: providerTimeZone,
            displayTimeZone: timeZone,
          })
        ) {
          return false
        }

        const adjustedEvent = getAdjustedEvent(args, startingRange.current, {
          skipRounding: flexibleAvailabilityWindows || isNPSession,
        })

        return !calendarRef.current
          ?.getApi()
          .getEvents()
          .filter((event) => isSessionOrSlot(event.extendedProps as EventExtendedProps))
          .some((event) =>
            eventsOverlap(
              {
                start: parseISO(event.startStr + 'Z'),
                end: parseISO(event.endStr + 'Z'),
              },
              {
                start: parseISO(adjustedEvent.start),
                end: parseISO(adjustedEvent.end),
              },
            ),
          )
      },
      [flexibleAvailabilityWindows, isNPSession, providerTimeZone, timeZone, workingHoursEnd, workingHoursStart],
    )

    useEffect(() => {
      document.getElementsByClassName('fc-timegrid-now-indicator-line').item(0)?.scrollIntoView({ block: 'center' })
    }, [])

    // Add working hours background band for supervisors with timezone selector.
    // Uses addEvent API instead of eventSources to avoid triggering eventSourceSuccess
    // (transformEvents), which expects start/end properties not present on recurring events.
    useEffect(() => {
      if (!(isTimezoneSelectorEnabled && onTimeZoneChange) || !calendarRef.current) {
        return
      }
      const api = calendarRef.current.getApi()
      api.getEventById('workingHours')?.remove()

      const viewDate = startDate ? new Date(startDate) : new Date()
      const projected = projectWorkingHoursToDisplayTimeZone(
        workingHoursStart,
        workingHoursEnd,
        providerTimeZone,
        timeZone,
        viewDate,
      )

      api.addEvent({
        startTime: projected.startTime,
        endTime: projected.endTime,
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        display: 'background',
        color: theme.colors.components.providerCalendar.event.background.background,
        id: 'workingHours',
      })

      return () => {
        api.getEventById('workingHours')?.remove()
      }
    }, [
      isTimezoneSelectorEnabled,
      onTimeZoneChange,
      timeZone,
      providerTimeZone,
      workingHoursStart,
      workingHoursEnd,
      startDate,
      theme.colors.components.providerCalendar.event.background.background,
    ])

    const eventSources = useMemo<EventSourceInput[]>(
      () => [
        {
          events: (info, _success, _failure) => {
            if (!getEvents) {
              return []
            }
            return getEvents({
              startDate: info.start.toISOString(),
              endDate: info.end.toISOString(),
              isLoadingPrimaryCalendar: true,
            })
          },
        },
      ],
      [getEvents],
    )

    const onSelect = useCallback(
      (event: DateSelectArg) => {
        if (!event.start || !event.end || !calendarRef.current) {
          return
        }
        const adjustedEvent = getAdjustedEvent(event, startingRange.current, {
          skipRounding: flexibleAvailabilityWindows || isNPSession,
        })
        if (
          !isWithinWorkingHours({
            event: adjustedEvent,
            workingHours: {
              start: workingHoursStart,
              end: workingHoursEnd,
            },
            timeZone: providerTimeZone,
            displayTimeZone: timeZone,
          })
        ) {
          calendarRef.current.getApi().unselect()
          return
        }
        addEventEntryPointRef.current = 'calendar'
        // start/startLocal are "fake UTC"; see toFakeUTCISOString docs
        setRangeToAdd({
          start: adjustedEvent.start,
          end: adjustedEvent.end,
          startLocal: adjustedEvent.start,
          endLocal: adjustedEvent.end,
        })
        const duration = differenceInMinutes(parseISO(adjustedEvent.end), parseISO(adjustedEvent.start)) / 60

        if (duration <= 1 && showIPCSessionCreate && showCalendarScheduleSession) {
          trackScheduleNewSessionEntryPoint?.('Free slot')
          setShowOpenSlotModal(true)
        } else {
          setShowAddEventModal(true)
        }
        calendarRef.current.getApi().unselect()
      },
      [
        timeZone,
        providerTimeZone,
        workingHoursStart,
        workingHoursEnd,
        showIPCSessionCreate,
        showCalendarScheduleSession,
        trackScheduleNewSessionEntryPoint,
        flexibleAvailabilityWindows,
        isNPSession,
      ],
    )

    const resetConflicts = useCallback(() => {
      if (!calendarRef.current) {
        return
      }
      const allEvents = calendarRef.current.getApi().getEvents()
      const hasConflictMap = new Map<string, boolean>()
      const nonDecorativeEvents = allEvents.filter((event) => event.id !== 'workingHours')
      const [sessionsAndSlots, otherEvents] = partition(nonDecorativeEvents, (event) =>
        isSessionOrSlot(event.extendedProps as EventExtendedProps),
      )

      // IGNORE_AVAILABILITY: only check sessions (availability excluded from conflict detection)
      const eventsToCheck =
        conflictStrategy === ConflictStrategy.IGNORE_AVAILABILITY
          ? sessionsAndSlots.filter((event) => (event.extendedProps as EventExtendedProps).lyraEventType === 'session')
          : sessionsAndSlots

      eventsToCheck.forEach((sessionOrSlot) => {
        otherEvents.forEach((event) => {
          const hasConflict = eventsOverlap(
            {
              start: parseISO(sessionOrSlot.startStr),
              end: parseISO(sessionOrSlot.endStr),
            },
            {
              start: parseISO(event.startStr),
              end: parseISO(event.endStr),
            },
          )

          if (hasConflict) {
            // EVENT_MARKER_ONLY: availability slots don't get conflict markers
            const isBookable = isBookableEvent(sessionOrSlot.extendedProps as EventExtendedProps)
            const showMarkerOnSessionOrSlot =
              conflictStrategy === ConflictStrategy.EVENT_MARKER_ONLY ? !isBookable : true
            hasConflictMap.set(sessionOrSlot.id, showMarkerOnSessionOrSlot)
            hasConflictMap.set(event.id, !isOOOEvent(event.title))
          }
        })
      })

      allEvents.forEach((event) => {
        const newValue = hasConflictMap.get(event.id) ?? false
        if (event.extendedProps.hasConflict !== newValue) {
          event.setExtendedProp('hasConflict', newValue)
        }
      })
    }, [conflictStrategy])

    const onEventChange = useCallback(
      ({ event, revert, oldEvent }: EventChangeArg) => {
        const extendedProps = event.extendedProps as EventExtendedProps
        const oldExtendedProps = oldEvent.extendedProps as EventExtendedProps
        if (
          event.startStr === oldEvent.startStr &&
          event.endStr === oldEvent.endStr &&
          (!showModalitySelection ||
            isSameModality(extendedProps.modality_availability, oldExtendedProps.modality_availability)) &&
          isSameBookingType(extendedProps.booking_type, oldExtendedProps.booking_type)
        ) {
          return
        }
        if (extendedProps.lyraEventType !== 'bookable' && extendedProps.lyraEventType !== 'bookable_recurring') {
          return
        }
        resetConflicts()
        onEventMoved?.(
          {
            externalId: extendedProps.externalId,
            startTime: toLocalISOString(event.startStr, timeZone),
            endTime: toLocalISOString(event.endStr, timeZone),
            modality: extendedProps.modality_availability,
            oldModality: oldExtendedProps.modality_availability,
            title: event.title,
            booking_type: extendedProps.booking_type,
          },
          revert,
        )
      },
      [onEventMoved, resetConflicts, showModalitySelection, timeZone],
    )

    const onEventRemove = useCallback(
      ({ event, revert }: EventRemoveArg) => {
        if (!event.extendedProps.externalId) {
          return
        }
        if (event.id === 'workingHours') {
          return
        }

        resetConflicts()
        // This prevents the onEventDeleted callback from being called on drag-and-drop or resize events, which also trigger an EventRemoveArg event.
        if (!isConfirmedEventRemovalRef.current) {
          return
        }
        onEventDeleted?.(
          {
            externalId: event.extendedProps.externalId,
          },
          revert,
        )
      },
      [onEventDeleted, resetConflicts],
    )

    const eventAllow = useCallback(
      (span: DateSpanApi) => {
        if (zonedTimeToUtc(parseISO(span.startStr), timeZone) < new Date()) {
          return false
        }
        return isWithinWorkingHours({
          event: { start: span.startStr, end: span.endStr },
          workingHours: { start: workingHoursStart, end: workingHoursEnd },
          timeZone: providerTimeZone,
          displayTimeZone: timeZone,
        })
      },
      [providerTimeZone, timeZone, workingHoursEnd, workingHoursStart],
    )

    const onAddTimeslotsConfirmation = useCallback(
      (eventsToBeAdded?: BuiltEvent[]) => {
        if (!calendarRef.current) {
          return
        }
        const events: (EventApi | null | undefined)[] = []
        if (isEmpty(eventsToBeAdded)) {
          return
        }
        eventsToBeAdded?.forEach((eventToBeAdded) => {
          events.push(
            calendarRef.current?.getApi().addEvent(
              {
                id: uuidv4(),
                title: eventToBeAdded.title ?? buildDefaultEventTitle(hybridBookingMode),
                start: eventToBeAdded.startTime,
                end: eventToBeAdded.endTime,
                ...(backgroundAvailability && { display: 'background' }),
                extendedProps: {
                  lyraEventType: 'bookable',
                  modality_availability: eventToBeAdded.modality,
                  booking_type: eventToBeAdded.booking_type,
                },
              },
              true,
            ),
          )
        })

        const nonNullEvents = events.filter((evt) => evt != null) as EventApi[]

        resetConflicts()
        onEventsAdded?.(
          nonNullEvents.map((event) => ({
            startTime: toLocalISOString(event.startStr, timeZone),
            endTime: toLocalISOString(event.endStr, timeZone),
            modality: event.extendedProps.modality_availability,
            title: event.title,
            booking_type: event.extendedProps.booking_type,
          })),
          () => {
            nonNullEvents.forEach((event) => {
              calendarRef.current?.getApi().getEventById(event.id)?.remove()
            })
          },
          (googleEvents) => {
            const sortedGoogleEvents = sortBy(googleEvents, (event) => parseISO(event.start.dateTime))
            nonNullEvents.forEach((event, i) => {
              const liveEvent = calendarRef.current?.getApi().getEventById(event.id)
              if (liveEvent) {
                liveEvent.setExtendedProp('externalId', sortedGoogleEvents[i].id)
              }
            })
          },
          addEventEntryPointRef.current,
        )
      },
      [backgroundAvailability, hybridBookingMode, onEventsAdded, resetConflicts, timeZone],
    )

    const onEventsSet = useCallback((events: EventApi[]) => {
      setStartDate(calendarRef.current?.getApi().view.currentStart.toISOString() ?? null)
      const sessions = events.filter((event) => {
        const extendedProps = event.extendedProps as EventExtendedProps
        return extendedProps.lyraEventType === 'session' && !isBendEvent(extendedProps)
      })
      const slots = events.filter((event) => isBookableEvent(event.extendedProps as EventExtendedProps))
      const newClientHours = sessions.length + slots.length
      setClientHours(newClientHours)

      const currentTime = new Date()

      const futureSessions = sessions.filter(
        (session) =>
          parseISO(session.startStr.endsWith('Z') ? session.startStr.slice(0, -1) : session.startStr) > currentTime,
      )
      setScheduledSessions(futureSessions.length)
      const newPastSessionApptIds = sessions
        .filter(
          (session) =>
            parseISO(session.startStr.endsWith('Z') ? session.startStr.slice(0, -1) : session.startStr) < currentTime,
        )
        .map((session) => session.extendedProps.lyraAppointmentId)

      setPastSessionApptIds(newPastSessionApptIds)

      const oooEvents = events.filter((event) => {
        const extendedProps = event.extendedProps as EventExtendedProps
        return isGenericEvent(extendedProps) && extendedProps.isOOOEvent
      })

      const timeOffMap = new Map<string, number>()
      oooEvents.forEach((event) => {
        const start = event.startStr
        const end = event.endStr
        const duration = differenceInMinutes(parseISO(end), parseISO(start)) / 60

        const dateStr = start.split('T')[0]
        timeOffMap.set(dateStr, (timeOffMap.get(dateStr) ?? 0) + duration)
      })

      const timeOffHours = Array.from(timeOffMap).reduce((acc, [_, hours]) => {
        return acc + Math.min(hours, 8)
      }, 0)
      setTimeOffHours(timeOffHours)

      const overlappingSlots = slots
        .filter((slot) =>
          oooEvents.some((oooEvent) =>
            eventsOverlap(
              {
                start: parseISO(oooEvent.startStr),
                end: parseISO(oooEvent.endStr),
              },
              {
                start: parseISO(slot.startStr),
                end: parseISO(slot.endStr),
              },
            ),
          ),
        )
        .map((slot) => (slot.extendedProps as BookableEventExtendedProps).externalId)
      setOverlappingSlots(overlappingSlots)
    }, [])

    const transformEvents = useCallback(
      (eventInputs: EventInput[]) => {
        const [sessionsAndSlots, otherEvents] = partition(eventInputs, (eventInput) =>
          isSessionOrSlot(eventInput.extendedProps as EventExtendedProps),
        )

        // IGNORE_AVAILABILITY: only check sessions (availability excluded from conflict detection)
        const eventsToCheck =
          conflictStrategy === ConflictStrategy.IGNORE_AVAILABILITY
            ? sessionsAndSlots.filter((e) => (e.extendedProps as EventExtendedProps).lyraEventType === 'session')
            : sessionsAndSlots

        eventsToCheck.forEach((sessionOrSlot) => {
          otherEvents.forEach((event) => {
            const hasConflict = eventsOverlap(
              {
                start: parseISO(sessionOrSlot.start as string),
                end: parseISO(sessionOrSlot.end as string),
              },
              {
                start: parseISO(event.start as string),
                end: parseISO(event.end as string),
              },
            )
            if (hasConflict) {
              // EVENT_MARKER_ONLY: availability slots don't get conflict markers
              const isBookable = isBookableEvent(sessionOrSlot.extendedProps as EventExtendedProps)
              const showMarkerOnSessionOrSlot =
                conflictStrategy === ConflictStrategy.EVENT_MARKER_ONLY ? !isBookable : true
              sessionOrSlot.extendedProps = {
                ...sessionOrSlot.extendedProps,
                hasConflict: showMarkerOnSessionOrSlot,
              }
              event.extendedProps = {
                ...event.extendedProps,
                hasConflict: !isOOOEvent(event.title),
              }
            }
          })
        })

        return eventInputs
      },
      [conflictStrategy],
    )

    useImperativeHandle(
      ref,
      () => ({
        updateAppointments: (newExtendedProps: { [key: number]: LyraEventExtendedPropsNoTZ }) =>
          // Full calendar library uses flushSync when calling setExtendedProp which will warn when about calling while already rendering
          // Fix found here: https://ah-mahir.medium.com/understanding-reacts-flushsync-warning-and-how-to-handle-it-with-settimeout-9442f6dd4ed9
          setTimeout(
            () =>
              calendarRef.current
                ?.getApi()
                .getEvents()
                .forEach((event) => {
                  const extendedProps = event.extendedProps as EventExtendedProps
                  if (
                    (extendedProps.lyraEventType === 'session' && !isBendEvent(extendedProps)) ||
                    isGenericEvent(extendedProps)
                  ) {
                    const appointmentId = extendedProps.lyraAppointmentId
                    if (appointmentId != null && appointmentId in newExtendedProps) {
                      Object.entries(newExtendedProps[appointmentId]).forEach(([key, value]) => {
                        if (event.extendedProps[key] !== value) {
                          event.setExtendedProp(key, value)
                        }
                      })
                    }
                  }
                }),
            0,
          ),
        refresh: () => calendarRef.current?.getApi().refetchEvents(),
      }),
      [],
    )

    const alerts = useMemo(() => {
      if (
        !getAlerts ||
        timeOffHours == null ||
        clientHours == null ||
        !startDate ||
        scheduledSessions == null ||
        overlappingSlots == null
      ) {
        return []
      }
      return getAlerts({ timeOffHours, clientHours, startDate, scheduledSessions, overlappingSlots })
    }, [clientHours, getAlerts, overlappingSlots, scheduledSessions, startDate, timeOffHours])

    const onAlertPressed = useCallback(
      (newAlertToShow: CalendarAlert) => {
        trackEvent?.(newAlertToShow.trackingEvent)
        setAlertsToShow((prev) => [...prev.filter((alert) => alert.type !== newAlertToShow.type), newAlertToShow])
      },
      [trackEvent],
    )

    const onAlertClose = useCallback(
      (alert: CalendarAlert) => {
        if (alert.closeTrackingAction) {
          trackEvent?.(COMMON_MIXPANEL_EVENTS.BUTTON_PRESS, {
            [COMMON_MIXPANEL_PROPERTIES.ACTION]: alert.closeTrackingAction,
          })
        }
        setAlertsToShow((prev) => prev.filter((alertToShow) => alertToShow.type !== alert.type))
      },
      [trackEvent],
    )

    useEffect(() => {
      setAlertsToShow([])
    }, [startDate])

    useEffect(() => {
      setAlertsToShow((prev) => {
        const newAlertsToShow: CalendarAlert[] = []
        alerts.forEach((alert) => {
          if (prev.some((alertToShow) => alertToShow.type === alert.type)) {
            newAlertsToShow.push(alert)
          }
        })

        return newAlertsToShow
      })
    }, [alerts])

    const updateEvent = (modalEvents: BuiltEvent[]) => {
      const updatedEvent = modalEvents[0]
      if (!!updatedEvent.modality) {
        eventToEdit?.event.setExtendedProp('modality_availability', updatedEvent.modality)
      }
      if (updatedEvent.booking_type !== undefined) {
        eventToEdit?.event.setExtendedProp('booking_type', updatedEvent.booking_type)
      }
      if (flexibleAvailabilityWindows || isNPSession) {
        eventToEdit?.event.setDates(updatedEvent.startTime, updatedEvent.endTime)
      } else {
        eventToEdit?.event.setStart(updatedEvent.startTime, { maintainDuration: true })
      }
      setEventToEdit(null)
    }

    const onAddSession = useCallback(
      async (event: {
        client: string
        startTime: string
        endTime: string
        slotEndTime: string
        duration: number
        meetingFormat: string
        episode?: Episode
        recurring: boolean
        frequency: number
        repeatFor: number
        address?: string
        videoPlatform?: VIDEO_PLATFORM
        sessionType?: string
      }) => {
        const localStartTime = toLocalISOString(event.startTime, timeZone)
        const localEndTime = toLocalISOString(event.endTime, timeZone)
        const localSlotEndTime = toLocalISOString(event.slotEndTime, timeZone)
        await onScheduleSession?.({
          ...event,
          localStartTime,
          localEndTime,
          localSlotEndTime,
        })
      },
      [onScheduleSession, timeZone],
    )

    return (
      <>
        <Container lowOpacity={loading}>
          <ProviderCalendarHeader
            rangeText={rangeText}
            onAddSlotPressed={() => {
              addEventEntryPointRef.current = 'add button'
              setShowAddEventModal(true)
              setInitialModalEvents(calendarRef.current && calendarRef.current.getApi().getEvents())
              setEventToEdit(null)
            }}
            onAddSessionPressed={() => {
              trackScheduleNewSessionEntryPoint?.('Add button')
              setShowScheduleSessionModal(true)
            }}
            onNextPressed={() => calendarRef.current?.getApi().next()}
            onPrevPressed={() => calendarRef.current?.getApi().prev()}
            onTodayPressed={() => calendarRef.current?.getApi().today()}
            onSettingsPressed={() => onSettingsPressed?.()}
            onOpenZendeskPressed={() => onOpenZendeskPressed?.()}
            scheduledSessions={scheduledSessions}
            clientHours={clientHours}
            alerts={alerts}
            onAlertPressed={onAlertPressed}
            hidePerformanceMetrics={hidePerformanceMetrics}
            hybridBookingMode={hybridBookingMode}
            showSessionSubmission={showSessionSubmission}
            sessionsAwaitingSubmission={sessionsAwaitingSubmission}
            rightHeaderSection={rightHeaderSection}
          />
          <FullCalendar
            ref={calendarRef}
            key={timeZone}
            headerToolbar={false}
            plugins={[timeGridWeek, interactionPlugin]}
            initialView='timeGridWeek'
            eventSources={eventSources}
            allDaySlot={false}
            dayHeaderContent={dayContentRenderer}
            nowIndicator
            now={nowInTimeZone}
            slotLabelContent={slotLabelContent}
            eventColor='transparent'
            selectable
            selectMirror
            select={onSelect}
            selectAllow={onSelectAllow}
            {...(flexibleAvailabilityWindows && { snapDuration: '00:15:00' })}
            eventChange={onEventChange}
            eventRemove={onEventRemove}
            eventsSet={onEventsSet}
            eventSourceSuccess={transformEvents}
            eventStartEditable
            eventAllow={eventAllow}
            eventOverlap={(stillEvent) => !isSessionOrSlot(stillEvent.extendedProps as EventExtendedProps)}
            slotEventOverlap={false}
            expandRows={true}
            contentHeight={contentHeight}
            stickyHeaderDates={true}
            eventClassNames={getEventClassNames}
            eventContent={getEventContent}
            timeZone={timeZone}
            viewDidMount={({ el }) => {
              viewElRef.current = el
              const axisEl = el?.querySelector('.fc-timegrid-axis-frame')
              if (axisEl) {
                setAxisElement(axisEl)
              }
            }}
            slotMinTime={resolvedMinTime}
            slotMaxTime={resolvedMaxTime}
            hiddenDays={hiddenDays}
          />
          <ProviderCalendarAlerts alerts={alertsToShow} onClose={onAlertClose} />
        </Container>
        {loading && (
          <LoadingView>
            <LoadingIndicator topPadding={0} />
          </LoadingView>
        )}
        <ProviderCalendarDateSlotSelectionModal
          onConfirmationButtonPress={(modalEvents: BuiltEvent[]) => {
            setShowAddEventModal(false)
            setRangeToAdd(null)
            eventToEdit ? updateEvent(modalEvents) : onAddTimeslotsConfirmation(modalEvents)
          }}
          defaultVideoPlatform={providerVideoPlatform}
          eventToEdit={eventToEdit}
          flexibleAvailabilityWindows={flexibleAvailabilityWindows}
          splitVideoOptions={splitVideoOptions}
          rangeToAdd={rangeToAdd}
          onRequestClose={() => {
            setShowAddEventModal(false)
            setRangeToAdd(null)
            setEventToEdit(null)
          }}
          showAddEventModal={showAddEventModal}
          showModalitySelection={showModalitySelection}
          setShowAddEventModal={setShowAddEventModal}
          getEvents={getEvents}
          initialEvents={initialModalEvents}
          inPersonPreference={inPersonPreference}
          videoPreference={videoPreference}
          isNPSession={isNPSession}
          addresses={providerAddresses}
          slotMaxTime={workingHoursEnd}
          slotMinTime={workingHoursStart}
          hiddenDays={hiddenDays}
        />
        <ProviderCalendarOpenSlotModal
          visible={showOpenSlotModal}
          onRequestClose={() => setShowOpenSlotModal(false)}
          suggestedClients={suggestedClients ?? []}
          clients={clients ?? []}
          defaultVideoPlatform={providerVideoPlatform}
          flexibleAvailabilityWindows={flexibleAvailabilityWindows}
          getEvents={getEvents}
          splitVideoOptions={splitVideoOptions}
          addresses={providerAddresses}
          inPersonPreference={inPersonPreference}
          videoPreference={videoPreference}
          showModalitySelection={showModalitySelection}
          slotMaxTime={workingHoursEnd}
          slotMinTime={workingHoursStart}
          onAddSlots={(events) => {
            onAddTimeslotsConfirmation(events)
            setRangeToAdd(null)
          }}
          onAddSession={onAddSession}
          rangeToAdd={rangeToAdd}
          getBookingDetailsForClient={getBookingDetailsForClient}
          isEngageDAProvider={engageDAProvider}
          enableBackdatedAppointments={enableBackdatedAppointments}
          isSelfPayEnabled={isSelfPayEnabled}
        />
        <ProviderCalendarScheduleSessionModal
          visible={showScheduleSessionModal}
          onRequestClose={() => {
            setShowScheduleSessionModal(false)
            setRangeToAdd(null)
            setSlotVideoPlatform(null)
          }}
          suggestedClients={suggestedClients ?? []}
          clients={clients ?? []}
          getEvents={getEvents}
          slotMaxTime={workingHoursEnd}
          slotMinTime={workingHoursStart}
          onAddSession={onAddSession}
          rangeToAdd={rangeToAdd}
          timeZone={timeZone}
          getBookingDetailsForClient={getBookingDetailsForClient}
          bookableInPersonLocations={providerAddresses}
          enableBackdatedAppointments={enableBackdatedAppointments}
          isSelfPayEnabled={isSelfPayEnabled}
          isLegacyProvider={isLegacyProvider}
          isLegacyClientListLoading={isLegacyClientListLoading}
          splitVideoOptions={splitVideoOptions}
          providerVideoPlatform={slotVideoPlatform ?? providerVideoPlatform}
          videoPreference={videoPreference}
          inPersonPreference={inPersonPreference}
        />
        <ProviderCalendarEditSessionModality
          showEditModal={showEditModalityModal}
          setShowEditModalityModal={setShowEditModalityModal}
          onRequestClose={() => {
            setShowEditModalityModal(false)
            reopenEventPopoverRef.current?.()
            reopenEventPopoverRef.current = null
          }}
          clientFirstName={eventToEdit?.event.extendedProps.clientFirstName ?? ''}
          clientLastName={eventToEdit?.event.extendedProps.clientLastName ?? ''}
          clientID={eventToEdit?.event.extendedProps.clientId ?? ''}
          startDate={eventToEdit ? toLocalISOString(eventToEdit.event.startStr, timeZone) : ''}
          endDate={eventToEdit ? toLocalISOString(eventToEdit.event.endStr, timeZone) : ''}
          timeZone={timeZone}
          appointmentId={eventToEdit?.event.extendedProps.lyraAppointmentId}
          currentMeetingFormat={eventToEdit?.event.extendedProps.appointmentMeetingFormat as MEETING_FORMATS}
          currentAppointmentAddress={eventToEdit?.event.extendedProps.appointmentAddress}
          currentVideoPlatform={eventToEdit?.event.extendedProps.appointmentVideoPlatform as VIDEO_PLATFORM | undefined}
          bookableInPersonLocations={providerAddresses}
          upcomingAppointmentIds={upcomingAppointmentIds}
          isLoadingUpcomingAppointmentIds={isLoadingUpcomingAppointmentIds}
          onUpdateModality={onUpdateModality}
          getBookingDetailsForClient={getBookingDetailsForClient}
          splitVideoOptions={splitVideoOptions}
        />
        <ConfirmationModal
          visible={!isNil(eventToDelete)}
          onRequestClose={() => setEventToDelete(null)}
          onConfirmationButtonPress={() => {
            if (!isNil(eventToDelete)) {
              isConfirmedEventRemovalRef.current = true
              try {
                eventToDelete.event.remove()
              } finally {
                isConfirmedEventRemovalRef.current = false
              }
            }
            setEventToDelete(null)
          }}
          modalTitle={
            <FormattedMessage
              defaultMessage='Delete availability this week?'
              description='Title of modal to delete one-time availability'
            />
          }
          cancelButtonText={<FormattedMessage defaultMessage='Cancel' description='Cancel button' />}
          confirmationButtonText={<FormattedMessage defaultMessage='Delete' description='Add availability button' />}
          modalContents={
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='Clients will not be able to book at this time.'
                  description='Body text for delete confirmation modal'
                />
              }
            />
          }
          testID={'DeleteCalendarAvailabilityModal'}
        />
        {/* Portal for timezone selector - renders into FullCalendar's axis element */}
        {axisElement &&
          isTimezoneSelectorEnabled &&
          onTimeZoneChange &&
          createPortal(
            <CalendarTimeZoneSelector
              selectedTimeZone={timeZone}
              providerTimeZone={calendarProvider?.timezone ?? timeZone}
              onTimeZoneSelected={onTimeZoneChange}
              testID={tID('ProviderCalendar-timezone-selector')}
            />,
            axisElement,
          )}
      </>
    )
  },
)
